import { useMemo, useState } from 'react';
import { recentWins } from './data/mockLots';
import type { Lot } from './data/mockLots';
import { HeroLot } from './components/HeroLot';
import { LiveLotCard } from './components/LiveLotCard';
import { StartingSoonCard } from './components/StartingSoonCard';
import { RecentWinCard } from './components/RecentWinCard';
import { SectionHeader } from './components/SectionHeader';
import { LotDetail } from './pages/LotDetail';
import { useLots } from './hooks/useLots';

export function App() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const lotsQuery = useLots();

  const buckets = lotsQuery.data;
  const allLots = useMemo<Lot[]>(
    () => (buckets ? [...buckets.live, ...buckets.scheduled] : []),
    [buckets],
  );

  const selected = useMemo(
    () => (selectedId ? allLots.find((l) => l.id === selectedId) : undefined),
    [selectedId, allLots],
  );

  if (selected) {
    return <LotDetail lot={selected} onBack={() => setSelectedId(null)} />;
  }

  const live = buckets?.live ?? [];
  const scheduled = buckets?.scheduled ?? [];
  const featured = live[0] ?? scheduled[0];
  const otherLive = featured ? live.filter((l) => l.id !== featured.id) : live;
  const startingSoon = scheduled.filter((l): l is Lot & { startsAt: number } => l.startsAt != null);

  return (
    <div className="min-h-dvh mx-auto max-w-[480px] relative">
      <header className="sticky top-0 z-40 bg-bg/85 backdrop-blur-md border-b border-edge">
        <div className="px-4 h-12 flex items-center justify-between">
          <span className="font-display font-bold text-[18px] tracking-tight text-fg">
            skin<span className="text-primary">step</span>
            <span className="text-accent">_</span>
          </span>
          <button
            type="button"
            className="font-mono text-[11px] tracking-wider text-fg flex items-center gap-1.5 border border-edge px-2.5 py-1 hover:border-primary/60 transition-colors"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-success anim-glow" />
            <span className="font-semibold">0</span>
            <span className="text-fg-muted">bids</span>
            <span className="text-primary ml-1">+</span>
          </button>
        </div>
      </header>

      <main className="pb-24">
        {lotsQuery.isPending && (
          <div className="px-4 pt-10 text-center">
            <p className="font-mono text-[11px] tracking-[0.25em] text-fg-muted">LOADING LOTS…</p>
          </div>
        )}

        {lotsQuery.isError && (
          <div className="mx-3 mt-6 border border-danger/40 bg-danger/5 px-4 py-6 text-center">
            <p className="font-mono text-[10px] tracking-[0.25em] text-danger">FAILED TO LOAD</p>
            <p className="text-[13px] text-fg-muted mt-2">
              {lotsQuery.error instanceof Error
                ? lotsQuery.error.message
                : 'Could not reach the auction service.'}
            </p>
            <button
              type="button"
              onClick={() => lotsQuery.refetch()}
              className="mt-3 font-mono text-[11px] text-primary border border-primary/40 px-3 py-1.5 hover:bg-primary/10"
            >
              RETRY
            </button>
          </div>
        )}

        {!lotsQuery.isPending && !lotsQuery.isError && !featured && (
          <div className="mx-3 mt-8 border border-dashed border-edge px-4 py-10 text-center bg-bg-elev/30">
            <p className="font-mono text-[10px] tracking-[0.25em] text-fg-muted">NO ACTIVE LOTS</p>
            <p className="text-[13px] text-fg-muted mt-2">
              The next batch is being prepared. Check back in a few minutes.
            </p>
          </div>
        )}

        {featured && (
          <div className="px-3 pt-4">
            <HeroLot lot={featured} onTap={() => setSelectedId(featured.id)} />
          </div>
        )}

        {otherLive.length > 0 && (
          <>
            <SectionHeader label="Live now" count={otherLive.length} accent="#ff4d4d" animate />
            <div className="px-3 flex flex-col gap-2">
              {otherLive.map((lot, i) => (
                <LiveLotCard key={lot.id} lot={lot} index={i} onSelect={() => setSelectedId(lot.id)} />
              ))}
            </div>
          </>
        )}

        {startingSoon.length > 0 && (
          <>
            <SectionHeader label="Starting soon" count={startingSoon.length} accent="#ffd93d" />
            <div className="snap-x-scroll no-scrollbar overflow-x-auto">
              <div className="flex gap-2.5 px-3 pb-2">
                {startingSoon.map((lot, i) => (
                  <StartingSoonCard
                    key={lot.id}
                    lot={lot}
                    index={i}
                    onSelect={() => setSelectedId(lot.id)}
                  />
                ))}
                <div className="w-2 shrink-0" />
              </div>
            </div>
          </>
        )}

        <SectionHeader label="Recent wins" count={recentWins.length} accent="#00ff94" />
        <div className="px-3 flex flex-col gap-2">
          {recentWins.map((w, i) => (
            <RecentWinCard key={w.id} win={w} index={i} />
          ))}
        </div>
      </main>

      <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] z-40 border-t border-edge bg-bg-deep/95 backdrop-blur-md">
        <div className="grid grid-cols-4 h-14">
          {[
            { k: 'Lots', i: '◆', active: true },
            { k: 'Mine', i: '◇' },
            { k: 'Wallet', i: '◊' },
            { k: 'Info', i: '?' },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              className={`flex flex-col items-center justify-center gap-0.5 text-[10px] tracking-wide transition-colors ${
                t.active ? 'text-primary' : 'text-fg-muted hover:text-fg'
              }`}
            >
              <span className="text-[15px] leading-none font-mono">{t.i}</span>
              <span>{t.k}</span>
              {t.active && <span className="w-5 h-px bg-primary mt-0.5" />}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
